<template>
    <div class="welcome">
        <div class="welcome__logo" :class="{'welcome__logo-islogo': featureLoginLogoEnabled}">
            <div v-show="featureLoginLogoEnabled" class="logo-pic"> 
                <publisher-icon class="logo-pic__img" :icon-name="'IconAuthorization'" />
            </div>

            <component :is="steps[activeStepIndex]" />

            <a
                id="welcomeNext"
                ref="refNext"
                role="button"
                class="btn"
                :href="nextButtonRoute"
                @focusin="onFocusIn"
                @focusout="onFocusOut"
                @click.prevent="increaseStep"
            >
                {{ nextText }}
            </a>
        </div>
    </div>

    <custom-modal
        v-if="isShownDialogModal"
        :title="dialogModal.title"
        :message="dialogModal.message"
        :buttons="dialogModal.buttons"
        :name="dialogModal.name"
        @on-btn-click="onModalClick"
    />
</template>

<script lang="ts" setup>
    import { ref, computed, onMounted, nextTick, type Ref, onBeforeUnmount } from "vue"
    import { useRouter, useRoute } from 'vue-router'
    import { Strings } from "@/locales/types/enums/strings";
    import { linkItems } from "@/components/Header/types";
    import { useI18n } from "vue-i18n";
    import FirstStep from "./components/FirstStep.vue";
    import SecondStep from "./components/SecondStep.vue";
    import type { ICustomModalState } from '@/composables/globalModals/types';
    import { useApplicationStore } from "@/stores/Application";
    import type { ExposeInterface } from "./types";
    import { ScreenName } from "@/common/types/screen-names";

    const ApplicationStore = useApplicationStore();
    const { deleteAllSelectedIdProgram } = ApplicationStore;
    
    const { $analytics, $navigator } = useNuxtApp();
    const router = useRouter();
    const route = useRoute();
    const { t } = useI18n();
    const { featureLoginLogoEnabled } = useAppConfig();

    const steps: ReturnType<typeof defineComponent>[] = [FirstStep, SecondStep];
    const nextText: string = t(Strings.LOGIN_NEXT).toString();
    const activeStepIndex = ref(0);
    const refNext = ref(null);
    const isShownDialogModal: Ref<boolean> = ref(false);
    const isRegKeyListeners: Ref<boolean> = ref(false);

    const nextButtonRoute = computed((): string => {
        if (activeStepIndex.value !== steps.length - 1) {
            return 'javascript:void(0)';
        }
        return linkItems.login.routerPath;
    })
    const dialogModal: ICustomModalState = {
        title: t(Strings.HOME_EXIT_APP).toString(),
        message: t(Strings.HOME_CONFIRM_EXIT_APP).toString(),
        buttons: [
            t(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE).toString(),
            t(Strings.SETTINGS_ACCOUNT_MODAL_NEGATIVE).toString()
        ],
        name: "exit",
    };

    const onModalClick = async (answer: string) => {
        if (answer === t(Strings.SETTINGS_ACCOUNT_MODAL_POSITIVE)) {
            window.close();
        } else {
            isShownDialogModal.value = false;
            focusFirstOption();
        }
    };
    const confirmExit = () => {
        try {
            decreaseStep();
        } catch (_) {
            isShownDialogModal.value = true;
        }
    };  
    const registerKeyListeners = () => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:back", confirmExit);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = () => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:back", confirmExit);
            isRegKeyListeners.value = false;
        }
    };
    const onFocusIn = (): void => {
        registerKeyListeners();
    }
    const onFocusOut = (): void => {
        unregisterKeyListeners();
    }
    const focusFirstOption = (): void => {
        (refNext.value as unknown as HTMLElement)?.focus();
    }
    const increaseStep = (): void => {
        if (activeStepIndex.value === steps.length - 1) {
            void router.push(linkItems.login.routerPath);
            return;
        }
        activeStepIndex.value++;
    }
    const decreaseStep = (): void => {
        if (!activeStepIndex.value) {
            throw new Error();
        } else {
            activeStepIndex.value--;
        }
    }
    const goToLastStep = (): void => {
        activeStepIndex.value = steps.length - 1;
    }



    onMounted(() => {
        const { query } = route;
        if (Object.prototype.hasOwnProperty.call(query, 'lastStep')) {
            goToLastStep();
            void router.replace({query: {}});
        }


       void nextTick((): void => {
            focusFirstOption();

            $analytics.createScreenEvent(ScreenName.WELCOME_PAGE, ScreenName.WELCOME_PAGE);
            deleteAllSelectedIdProgram();
        })
    });

    onBeforeUnmount(() => {
        unregisterKeyListeners();
    });

    defineExpose<ExposeInterface>({
        decreaseStep,
        goToLastStep,
        focusFirstOption
    });

</script>

<style lang="scss">
    @import url("styles.scss");
</style>